/**
 * FileUpload Component Suite
 * Enterprise-grade file upload components optimized for Supabase integration
 */

// Main component
export { FileUploadField } from './FileUploadField'

// Sub-components
export { FileDropZone, FileDropZoneContent, FileDropZoneCompact, FileDropZoneWithPreview } from './components/FileDropZone'
export { FileThumbnail, FileThumbnailEnhanced, FileThumbnailGrid } from './components/FileThumbnail'
export { FileList, FileListGrouped, DraggableFileList, VirtualFileList, SearchableFileList } from './components/FileList'
export { FileItem, DraggableFileItem } from './components/FileItem'
export { UploadProgressBar, BatchProgress, CancellableProgress } from './components/UploadProgress'
export { ValidationMessages, ValidationMessageCompact, ValidationSummary, InlineValidation } from './components/ValidationMessages'
export { FilePreviewDialog } from './components/FilePreviewDialog'

// Hooks
export { useFileUploadUI } from './hooks/useFileUploadUI'
export type { UseFileUploadUIProps, UseFileUploadUIReturn } from './hooks/useFileUploadUI'

// Types
export type {
  // Main component types
  FileUploadConfig,
  FileUploadFieldProps,
  FileUploadState,
  FileUploadVariant,
  ValidationPosition,
  FileStatus,
  FileUploadEvent,
  FileUploadPresets,

  // File types
  FileWithUIState,
  UploadProgress,
  UploadResult,
  ValidationResult,

  // Hook interfaces (for Supabase integration)
  FileUploadHook,
  BatchUploadHook,
  FileValidationHook,

  // Sub-component types
  FileDropZoneProps,
  FileThumbnailProps,
  FileListProps,
  FileItemProps,
  UploadProgressProps,
  ValidationMessagesProps,
  FilePreviewDialogProps
} from './types'

// Utilities
export {
  // File utilities
  generateFileId,
  createFileWithUIState,
  validateFile,
  validateFiles,
  findDuplicateFiles,
  formatFileSize,
  getFileTypeCategory,
  getFileExtension,
  isImageFile,
  canGenerateThumbnail,
  createPreviewUrl,
  cleanupPreviewUrl,
  fileToBase64,
  getFileIcon,
  calculateUploadSpeed,
  estimateTimeRemaining,
  formatDuration,
  debounce,
  throttle,
  moveArrayItem,
  removeArrayItem,
  replaceArrayItem
} from './utils/fileUtils'

export {
  // Thumbnail utilities
  generateImageThumbnail,
  generateVideoThumbnail,
  generateIconThumbnail,
  generateThumbnailCached,
  clearThumbnailCache,
  cleanupThumbnail
} from './utils/thumbnailUtils'

export type {
  ThumbnailOptions,
  ThumbnailResult
} from './utils/thumbnailUtils'

// Configuration presets for common use cases
export const fileUploadPresets: FileUploadPresets = {
  images: {
    accept: ['image/*'],
    maxSize: 10 * 1024 * 1024, // 10MB
    maxFiles: 10,
    multiple: true,
    showThumbnails: true,
    variant: 'gallery',
    dragDropText: 'Drop images here',
    browseText: 'browse images',
    validationPosition: 'bottom'
  },

  documents: {
    accept: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    maxSize: 50 * 1024 * 1024, // 50MB
    maxFiles: 5,
    multiple: true,
    showThumbnails: true,
    variant: 'default',
    dragDropText: 'Drop documents here',
    browseText: 'browse documents',
    validationPosition: 'bottom'
  },

  media: {
    accept: ['image/*', 'video/*', 'audio/*'],
    maxSize: 100 * 1024 * 1024, // 100MB
    maxFiles: 20,
    multiple: true,
    showThumbnails: true,
    variant: 'gallery',
    dragDropText: 'Drop media files here',
    browseText: 'browse media',
    validationPosition: 'bottom'
  },

  archives: {
    accept: ['application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed'],
    maxSize: 200 * 1024 * 1024, // 200MB
    maxFiles: 3,
    multiple: true,
    showThumbnails: false,
    variant: 'default',
    dragDropText: 'Drop archive files here',
    browseText: 'browse archives',
    validationPosition: 'bottom'
  },

  profilePicture: {
    accept: ['image/jpeg', 'image/png', 'image/webp'],
    maxSize: 5 * 1024 * 1024, // 5MB
    maxFiles: 1,
    multiple: false,
    showThumbnails: true,
    variant: 'compact',
    dragDropText: 'Drop your profile picture here',
    browseText: 'choose picture',
    validationPosition: 'bottom'
  },

  gallery: {
    accept: ['image/*'],
    maxSize: 20 * 1024 * 1024, // 20MB
    maxFiles: 50,
    multiple: true,
    showThumbnails: true,
    variant: 'gallery',
    dragDropText: 'Drop photos here',
    browseText: 'add photos',
    validationPosition: 'bottom',
    autoUpload: true
  }
}